import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtDate, toISODate } from "@/lib/format";
import { BulkGrid } from "@/components/BulkGrid";
import { Plus, Loader2, Pencil, Trash2, UsersRound, ClipboardPaste, Rows3, PencilRuler, ArrowUp, ArrowDown, ChevronsUpDown, UserX, RotateCcw, MessageSquareText, CheckCircle2 } from "lucide-react";

export const DEPARTMENTS = [
  "Accounting", "Assistant Manager", "Bartender", "Breakfast", "Cook",
  "Executive", "Front Desk", "General Manager", "Houseman", "Housekeeping",
  "Laundry", "Maintenance", "Night Auditor", "Sales", "Server",
];

const EMPTY = { first_name: "", last_name: "", department: "Front Desk", hire_date: "", birthday: "", rate_of_pay: "", pay_type: "hourly", annual_salary: "", phone: "", email: "", ssn_last4: "", positions: [] };

const EMPTY_BULK = { last_name: "", first_name: "", department: "", hire_date: "", rate_of_pay: "", annual_salary: "", phone: "", email: "" };
const newBulkRows = (n = 8) => Array.from({ length: n }, () => ({ ...EMPTY_BULK }));

export default function Employees() {
  const { user } = useAuth();
  const canManage = can(user, ["admin", "regional", "hr", "gm"]);


  const [props, setProps] = useState([]);
  const { activePropertyId: propertyId, setActivePropertyId: setPropertyId } = useActiveProperty();
  const [list, setList] = useState(null);
  const [tab, setTab] = useState("active");
  const [termFor, setTermFor] = useState(null); // employee being terminated
  const [termForm, setTermForm] = useState({ term_date: toISODate(new Date()), term_reason: "" });
  const [reviewFor, setReviewFor] = useState(null); // employee whose reviews are open
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewForm, setReviewForm] = useState({ title: "", message: "" });
  const [reviewSaving, setReviewSaving] = useState(false);
  const [termSaving, setTermSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState(DEPARTMENTS);
  const [deptOpen, setDeptOpen] = useState(false);
  const [newDept, setNewDept] = useState("");
  const [deptSaving, setDeptSaving] = useState(false);

  const [ptoMap, setPtoMap] = useState({});
  const [ptoEnabled, setPtoEnabled] = useState(false);
  const [sickMap, setSickMap] = useState({});
  const [sickEnabled, setSickEnabled] = useState(false);
  const [hbAcks, setHbAcks] = useState({});
  const [hbCurrent, setHbCurrent] = useState(null);

  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sortedList = useMemo(() => {
    if (!list) return list;
    const val = (e) =>
      sort.key === "department"
        ? (e.department || "").toLowerCase()
        : `${e.first_name || ""} ${e.last_name || ""}`.trim().toLowerCase();
    const arr = [...list].sort((a, b) => val(a).localeCompare(val(b)));
    return sort.dir === "desc" ? arr.reverse() : arr;
  }, [list, sort]);
  const SortIcon = ({ col }) =>
    sort.key !== col ? <ChevronsUpDown className="ml-1 inline h-3.5 w-3.5 text-muted-foreground/60" />
      : sort.dir === "asc" ? <ArrowUp className="ml-1 inline h-3.5 w-3.5 text-primary" />
      : <ArrowDown className="ml-1 inline h-3.5 w-3.5 text-primary" />;

  const matchDept = useCallback((raw) => {
    const t = (raw || "").trim().toLowerCase();
    if (!t) return "";
    const exact = departments.find((d) => d.toLowerCase() === t);
    if (exact) return exact;
    const partial = departments.find((d) => d.toLowerCase().startsWith(t) || t.startsWith(d.toLowerCase()));
    return partial || raw.trim();
  }, [departments]);

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProps(r.data);
      if (!propertyId && r.data[0]) setPropertyId(r.data[0].id);
    });
    api.get("/employees/departments").then((r) => setDepartments(r.data.departments)).catch(() => {});
  }, []);

  const addDepartment = async () => {
    const name = newDept.trim();
    if (!name) return;
    setDeptSaving(true);
    try {
      const { data } = await api.post("/employees/departments", { name });
      setDepartments(data.departments);
      toast.success(`Added "${name}"`);
      setNewDept("");
      setDeptOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeptSaving(false);
    }
  };

  const load = useCallback(() => {
    if (!propertyId) return;
    setList(null);
    api.get("/employees", { params: { property_id: propertyId, status: tab === "active" ? "active" : "terminated" } }).then((r) => setList(r.data)).catch(() => setList([]));
    api.get("/pto-balances", { params: { property_id: propertyId } })
      .then((r) => {
        setPtoEnabled(!!r.data.config?.enabled);
        setSickEnabled(!!r.data.sick_config?.enabled);
        const m = {}, s = {};
        (r.data.employees || []).forEach((e) => {
          m[e.employee_id] = e.balance;
          s[e.employee_id] = { balance: e.sick_balance, enabled: e.sick_enabled };
        });
        setPtoMap(m);
        setSickMap(s);
      })
      .catch(() => { setPtoEnabled(false); setPtoMap({}); setSickEnabled(false); setSickMap({}); });
    api.get("/handbooks/employee-acks", { params: { property_id: propertyId } })
      .then((r) => { setHbCurrent(r.data.current); setHbAcks(r.data.acks || {}); })
      .catch(() => { setHbCurrent(null); setHbAcks({}); });
  }, [propertyId, tab]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [leaveLog, setLeaveLog] = useState([]);

  const adjustLeave = async (eid, kind, current) => {
    const label = kind === "pto" ? "PTO" : "Sick";
    const input = window.prompt(`Set correct ${label} balance (hrs):`, String(current ?? 0));
    if (input == null) return;
    const target = Number(input);
    if (Number.isNaN(target) || target < 0) return toast.error("Enter a valid number");
    const reason = window.prompt("Reason for this change (for the audit log):", "") || "";
    try {
      const { data } = await api.post(`/employees/${eid}/adjust-leave`, { kind, target_balance: target, reason });
      toast.success(`${label} balance set to ${data.balance} hrs`);
      if (editId === eid) api.get(`/employees/${eid}/leave-log`).then((r) => setLeaveLog(r.data)).catch(() => {});
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const viewSignedHandbook = async (hid) => {
    try {
      const res = await api.get(`/handbooks/${hid}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Could not open the handbook file");
    }
  };
  const HandbookChip = ({ eid }) => {
    if (!hbCurrent) return <span className="text-xs text-muted-foreground">No handbook</span>;
    const a = hbAcks[eid];
    if (!a) return <Badge variant="outline" className="border-red-300 bg-red-50 text-red-600 dark:border-red-900 dark:bg-red-950 dark:text-red-400" data-testid={`hb-chip-${eid}`}>Not signed</Badge>;
    if (a.is_current) return <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-400" data-testid={`hb-chip-${eid}`}>Signed ✓ {a.signed_at ? fmtDate(a.signed_at) : ""}</Badge>;
    return <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-400" data-testid={`hb-chip-${eid}`}>Signed v{a.version} · outdated</Badge>;
  };

  const addPosition = () => setForm((f) => ({ ...f, positions: [...(f.positions || []), { department: "", rate_of_pay: "" }] }));
  const setPosition = (idx, k, v) => setForm((f) => ({ ...f, positions: (f.positions || []).map((p, i) => (i === idx ? { ...p, [k]: v } : p)) }));
  const removePosition = (idx) => setForm((f) => ({ ...f, positions: (f.positions || []).filter((_, i) => i !== idx) }));

  const openNew = () => { setEditId(null); setForm(EMPTY); setLeaveLog([]); setOpen(true); };
  const openEdit = (e) => {
    setEditId(e.id);
    setLeaveLog([]);
    api.get(`/employees/${e.id}/leave-log`).then((r) => setLeaveLog(r.data)).catch(() => setLeaveLog([]));
    setForm({ first_name: e.first_name, last_name: e.last_name, department: e.department, hire_date: e.hire_date || "", birthday: e.birthday || "", rate_of_pay: e.rate_of_pay ?? "", pay_type: e.pay_type || "hourly", annual_salary: e.annual_salary ?? "", phone: e.phone || "", email: e.email || "", ssn_last4: e.ssn_last4 || "", positions: (e.positions || []).map((p) => ({ department: p.department || "", rate_of_pay: p.rate_of_pay ?? "" })) });
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        property_id: propertyId, ...form,
        rate_of_pay: form.pay_type === "salaried" || form.rate_of_pay === "" ? null : Number(form.rate_of_pay),
        annual_salary: form.pay_type === "salaried" && form.annual_salary !== "" ? Number(form.annual_salary) : null,
        positions: (form.positions || [])
          .filter((p) => p.department && p.department !== form.department)
          .map((p) => ({ department: p.department, rate_of_pay: p.rate_of_pay === "" || p.rate_of_pay == null ? null : Number(p.rate_of_pay) })),
      };
      if (editId) await api.patch(`/employees/${editId}`, payload);
      else await api.post("/employees", payload);
      toast.success(editId ? "Employee updated" : "Employee added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete ${e.first_name} ${e.last_name}? This permanently removes them and their logged hours.`)) return;
    await api.delete(`/employees/${e.id}`);
    toast.success("Employee deleted");
    load();
  };

  const openTerminate = (e) => {
    setTermFor(e);
    setTermForm({ term_date: toISODate(new Date()), term_reason: "" });
  };
  const submitTerminate = async () => {
    if (!termForm.term_reason.trim()) { toast.error("Please enter a reason"); return; }
    setTermSaving(true);
    try {
      await api.post(`/employees/${termFor.id}/terminate`, { term_date: termForm.term_date, term_reason: termForm.term_reason.trim() });
      toast.success(`${termFor.first_name} ${termFor.last_name} marked non-active`);
      setTermFor(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTermSaving(false);
    }
  };
  const reactivate = async (e) => {
    if (!window.confirm(`Reactivate ${e.first_name} ${e.last_name}?`)) return;
    await api.post(`/employees/${e.id}/reactivate`);
    toast.success("Employee reactivated");
    load();
  };

  const openReviews = async (e) => {
    setReviewFor(e);
    setReviewForm({ title: "", message: "" });
    setReviews([]);
    setReviewsLoading(true);
    try {
      const r = await api.get(`/employees/${e.id}/reviews`);
      setReviews(r.data);
    } catch { /* ignore */ } finally { setReviewsLoading(false); }
  };
  const submitReview = async () => {
    if (!reviewForm.title.trim() || !reviewForm.message.trim()) { toast.error("Add a title and message"); return; }
    setReviewSaving(true);
    try {
      const r = await api.post(`/employees/${reviewFor.id}/reviews`, { title: reviewForm.title.trim(), message: reviewForm.message.trim() });
      setReviews((rv) => [r.data, ...rv]);
      setReviewForm({ title: "", message: "" });
      toast.success("Review sent — employee will acknowledge at next clock-in");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setReviewSaving(false); }
  };
  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/reviews/${id}`);
    setReviews((rv) => rv.filter((x) => x.id !== id));
  };

  // ---- Bulk grids (add + edit) ----
  const bulkCols = useMemo(() => [
    { key: "last_name", label: "Last Name", w: "w-32" },
    { key: "first_name", label: "First Name", w: "w-32" },
    { key: "department", label: "Department", w: "w-40", options: departments, transform: matchDept },
    { key: "hire_date", label: "Hire Date", w: "w-32", type: "date", transform: toISODate },
    { key: "rate_of_pay", label: "Rate ($/hr)", w: "w-24" },
    { key: "annual_salary", label: "Annual Salary ($)", w: "w-28" },
    { key: "phone", label: "Phone", w: "w-32" },
    { key: "email", label: "Email", w: "w-48" },
  ], [departments, matchDept]);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState(newBulkRows());
  const [bulkSaving, setBulkSaving] = useState(false);

  const openBulk = () => { setBulkRows(newBulkRows()); setBulkOpen(true); };
  const addBulkRows = () => setBulkRows((rows) => [...rows, ...newBulkRows(5)]);

  const saveBulk = async () => {
    const rows = bulkRows
      .filter((r) => r.first_name.trim() || r.last_name.trim())
      .map((r) => ({
        ...r,
        hire_date: toISODate(r.hire_date),
        pay_type: (Number(r.annual_salary) || 0) > 0 && !r.rate_of_pay ? "salaried" : "hourly",
        rate_of_pay: r.rate_of_pay === "" ? null : Number(r.rate_of_pay),
        annual_salary: r.annual_salary === "" ? null : Number(r.annual_salary),
      }));
    if (rows.length === 0) { toast.error("Enter at least one employee (first & last name)"); return; }
    setBulkSaving(true);
    try {
      const { data } = await api.post("/employees/bulk", { property_id: propertyId, employees: rows });
      if (data.errors?.length) {
        toast.warning(`Added ${data.inserted}. Skipped: ${data.errors.map((e) => `row ${e.row} (${e.error})`).join("; ")}`);
      } else {
        toast.success(`Added ${data.inserted} employee${data.inserted !== 1 ? "s" : ""}`);
      }
      if (data.inserted > 0) { setBulkOpen(false); load(); }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBulkSaving(false);
    }
  };

  // ---- Bulk edit (prefilled with existing employees) ----
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditRows, setBulkEditRows] = useState([]);
  const [bulkEditSaving, setBulkEditSaving] = useState(false);

  const openBulkEdit = () => {
    setBulkEditRows((list || []).map((e) => ({
      id: e.id,
      last_name: e.last_name || "",
      first_name: e.first_name || "",
      department: e.department || "",
      hire_date: toISODate(e.hire_date),
      rate_of_pay: e.rate_of_pay ?? "",
      annual_salary: e.annual_salary ?? "",
      phone: e.phone || "",
      email: e.email || "",
    })));
    setBulkEditOpen(true);
  };

  const saveBulkEdit = async () => {
    const rows = bulkEditRows
      .filter((r) => r.id)
      .map((r) => ({
        id: r.id,
        last_name: r.last_name, first_name: r.first_name, department: r.department,
        hire_date: toISODate(r.hire_date),
        pay_type: (Number(r.annual_salary) || 0) > 0 && !r.rate_of_pay ? "salaried" : "hourly",
        rate_of_pay: r.rate_of_pay === "" ? null : Number(r.rate_of_pay),
        annual_salary: r.annual_salary === "" ? null : Number(r.annual_salary),
        phone: r.phone, email: r.email,
      }));
    setBulkEditSaving(true);
    try {
      const { data } = await api.post("/employees/bulk-update", { property_id: propertyId, employees: rows });
      if (data.errors?.length) {
        toast.warning(`Updated ${data.updated}. Skipped: ${data.errors.map((e) => `row ${e.row} (${e.error})`).join("; ")}`);
      } else {
        toast.success(`Updated ${data.updated} employee${data.updated !== 1 ? "s" : ""}`);
      }
      setBulkEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBulkEditSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="employees-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">People</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Employees</h1>
          <p className="mt-1 text-sm text-muted-foreground">Roster used for payroll analysis and scheduling.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="w-56" data-testid="emp-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
            <SelectContent>{props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
          </Select>
          {canManage && tab === "active" && (
            <Button variant="outline" onClick={openBulk} disabled={!propertyId} data-testid="bulk-add-employee-btn">
              <ClipboardPaste className="mr-2 h-4 w-4" /> Bulk Add (Paste)
            </Button>
          )}
          {canManage && tab === "active" && (
            <Button variant="outline" onClick={openBulkEdit} disabled={!propertyId || !list?.length} data-testid="bulk-edit-employee-btn">
              <PencilRuler className="mr-2 h-4 w-4" /> Bulk Edit
            </Button>
          )}
          {canManage && tab === "active" && (
            <Button onClick={openNew} disabled={!propertyId} data-testid="add-employee-btn">
              <Plus className="mr-2 h-4 w-4" /> Add Employee
            </Button>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="employee-tabs">
          <TabsTrigger value="active" data-testid="tab-active">Active</TabsTrigger>
          <TabsTrigger value="inactive" data-testid="tab-inactive">Non-active</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="rounded-none border">
        {list === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center">
            <UsersRound className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">{tab === "active" ? "No active employees yet." : "No terminated employees."}</p>
          </div>
        ) : tab === "inactive" ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Term Date</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Terminated By</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedList.map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.id}`}>
                  <TableCell className="font-medium">{e.first_name} {e.last_name}</TableCell>
                  <TableCell><Badge variant="secondary" className="rounded-none">{e.department}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">{e.term_date ? fmtDate(e.term_date) : "—"}</TableCell>
                  <TableCell className="max-w-xs whitespace-pre-wrap text-muted-foreground" data-testid={`term-reason-${e.id}`}>{e.term_reason || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{e.terminated_by || "—"}</TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => reactivate(e)} data-testid={`reactivate-employee-${e.id}`}>
                        <RotateCcw className="mr-2 h-3.5 w-3.5" /> Reactivate
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button onClick={() => toggleSort("name")} className="flex items-center font-semibold hover:text-primary" data-testid="sort-name">
                    Name <SortIcon col="name" />
                  </button>
                </TableHead>
                <TableHead>
                  <button onClick={() => toggleSort("department")} className="flex items-center font-semibold hover:text-primary" data-testid="sort-department">
                    Department <SortIcon col="department" />
                  </button>
                </TableHead>
                <TableHead>Hire Date</TableHead>
                <TableHead>Pay</TableHead>
                <TableHead className="text-right">PTO Balance</TableHead>
                <TableHead className="text-right">Sick Balance</TableHead>
                <TableHead>Handbook</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedList.map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.id}`}>
                  <TableCell className="font-medium">{e.first_name} {e.last_name}</TableCell>
                  <TableCell><Badge variant="secondary" className="rounded-none">{e.department}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">{e.hire_date ? fmtDate(e.hire_date) : "—"}</TableCell>
                  <TableCell className="font-mono">
                    {e.pay_type === "salaried"
                      ? (e.annual_salary != null ? <span>${Number(e.annual_salary).toLocaleString()}<span className="text-xs text-muted-foreground">/yr</span></span> : "—")
                      : (e.rate_of_pay != null ? <span>${Number(e.rate_of_pay).toFixed(2)}<span className="text-xs text-muted-foreground">/hr</span></span> : "—")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" data-testid={`employee-pto-${e.id}`}>
                    {ptoEnabled
                      ? <button onClick={() => adjustLeave(e.id, "pto", ptoMap[e.id] || 0)} className="font-medium text-emerald-600 underline-offset-2 hover:underline dark:text-emerald-400" title="Click to correct">{Number(ptoMap[e.id] || 0).toFixed(1)} h</button>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" data-testid={`employee-sick-${e.id}`}>
                    {(sickEnabled || (sickMap[e.id]?.balance || 0) > 0)
                      ? <button onClick={() => adjustLeave(e.id, "sick", sickMap[e.id]?.balance || 0)} className="font-medium text-sky-600 underline-offset-2 hover:underline dark:text-sky-400" title="Click to correct">{Number(sickMap[e.id]?.balance || 0).toFixed(1)} h</button>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell><HandbookChip eid={e.id} /></TableCell>
                  <TableCell className="text-muted-foreground">{e.phone || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{e.email || "—"}</TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => openReviews(e)} title="Reviews" data-testid={`reviews-employee-${e.id}`}><MessageSquareText className="h-4 w-4 text-blue-600" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(e)} data-testid={`edit-employee-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openTerminate(e)} title="Terminate / mark non-active" data-testid={`terminate-employee-${e.id}`}><UserX className="h-4 w-4 text-amber-600" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit Employee" : "Add Employee"}</DialogTitle>
            <DialogDescription>Enter the employee's details.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Last Name</Label>
              <Input value={form.last_name} onChange={(e) => setF("last_name", e.target.value)} data-testid="emp-last-name" />
            </div>
            <div className="space-y-1.5">
              <Label>First Name</Label>
              <Input value={form.first_name} onChange={(e) => setF("first_name", e.target.value)} data-testid="emp-first-name" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Department</Label>
                <button type="button" onClick={() => setDeptOpen(true)} className="text-xs font-medium text-primary hover:underline" data-testid="add-department-link">+ Add department</button>
              </div>
              <Select value={form.department} onValueChange={(v) => setF("department", v)}>
                <SelectTrigger data-testid="emp-department"><SelectValue /></SelectTrigger>
                <SelectContent>{departments.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Hire Date</Label>
              <Input type="date" value={form.hire_date} onChange={(e) => setF("hire_date", e.target.value)} data-testid="emp-hire-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Birthday</Label>
              <Input type="date" value={form.birthday} onChange={(e) => setF("birthday", e.target.value)} data-testid="emp-birthday" />
            </div>
            <div className="space-y-1.5">
              <Label>Pay Type</Label>
              <Select value={form.pay_type} onValueChange={(v) => setF("pay_type", v)}>
                <SelectTrigger data-testid="emp-pay-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="salaried">Salaried</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.pay_type === "salaried" ? (
              <div className="space-y-1.5">
                <Label>Annual Salary ($)</Label>
                <Input type="text" inputMode="decimal" value={form.annual_salary} onChange={(e) => setF("annual_salary", e.target.value)} data-testid="emp-salary" />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Rate of Pay ($/hr)</Label>
                <Input type="text" inputMode="decimal" value={form.rate_of_pay} onChange={(e) => setF("rate_of_pay", e.target.value)} data-testid="emp-rate" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setF("phone", e.target.value)} data-testid="emp-phone" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setF("email", e.target.value)} data-testid="emp-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Last 4 of SSN <span className="text-xs text-muted-foreground">(for kiosk login without a phone)</span></Label>
              <Input value={form.ssn_last4} inputMode="numeric" maxLength={4} placeholder="1234"
                onChange={(e) => setF("ssn_last4", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="emp-ssn4" />
            </div>
            <div className="col-span-2 space-y-2 border-t border-border pt-3">
              <div className="flex items-center justify-between">
                <Label>Additional Positions <span className="text-xs text-muted-foreground">(they pick one when clocking in)</span></Label>
                <button type="button" onClick={addPosition} className="text-xs font-medium text-primary hover:underline" data-testid="add-position-link">+ Add position</button>
              </div>
              {(form.positions || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No extra positions. Primary is <span className="font-medium text-foreground">{form.department}</span> at {form.pay_type === "salaried" ? "salary" : `$${form.rate_of_pay || "0"}/hr`}.</p>
              ) : (
                (form.positions || []).map((p, idx) => (
                  <div key={idx} className="flex items-end gap-2" data-testid={`position-row-${idx}`}>
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs">Position</Label>
                      <Select value={p.department} onValueChange={(v) => setPosition(idx, "department", v)}>
                        <SelectTrigger data-testid={`position-dept-${idx}`}><SelectValue placeholder="Select department" /></SelectTrigger>
                        <SelectContent>{departments.filter((d) => d !== form.department).map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}</SelectContent>
                      </Select>
                    </div>
                    <div className="w-28 space-y-1">
                      <Label className="text-xs">Rate ($/hr)</Label>
                      <Input inputMode="decimal" placeholder="same" value={p.rate_of_pay ?? ""}
                        onChange={(e) => setPosition(idx, "rate_of_pay", e.target.value.replace(/[^0-9.]/g, ""))} data-testid={`position-rate-${idx}`} />
                    </div>
                    <Button type="button" size="icon" variant="ghost" onClick={() => removePosition(idx)} data-testid={`position-remove-${idx}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
          {editId && (
            <div className="rounded-lg border border-border bg-muted/30 p-4" data-testid="employee-handbook-record">
              <div className="mb-2 flex items-center justify-between">
                <Label className="text-sm font-bold">Employee Handbook</Label>
                {!hbCurrent && <span className="text-xs text-muted-foreground">No handbook published yet</span>}
              </div>
              {hbCurrent && (hbAcks[editId] ? (
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    {hbAcks[editId].is_current
                      ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-400">Signed ✓</Badge>
                      : <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-400">Signed an older version</Badge>}
                    <span className="text-muted-foreground">{hbAcks[editId].signed_at ? fmtDate(hbAcks[editId].signed_at) : ""}</span>
                  </div>
                  <div><span className="text-muted-foreground">Document: </span>{hbAcks[editId].title} <span className="text-muted-foreground">(v{hbAcks[editId].version})</span></div>
                  <div><span className="text-muted-foreground">Signature: </span><span className="font-medium italic">{hbAcks[editId].typed_name || "—"}</span></div>
                  {!hbAcks[editId].is_current && hbCurrent && (
                    <div className="text-xs text-amber-600 dark:text-amber-400">Current version is v{hbCurrent.version} — employee should re-sign.</div>
                  )}
                  <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => viewSignedHandbook(hbAcks[editId].handbook_id)} data-testid="view-signed-handbook-btn">
                    View signed handbook
                  </Button>
                </div>
              ) : (
                <div className="space-y-1 text-sm">
                  <Badge variant="outline" className="border-red-300 bg-red-50 text-red-600 dark:border-red-900 dark:bg-red-950 dark:text-red-400">Not signed</Badge>
                  <p className="text-xs text-muted-foreground">This employee will be asked to review &amp; sign "{hbCurrent.title}" (v{hbCurrent.version}) the next time they clock in on the kiosk.</p>
                </div>
              ))}
            </div>
          )}
          {editId && leaveLog.length > 0 && (
            <div className="rounded-lg border border-border bg-muted/30 p-4" data-testid="leave-log-section">
              <Label className="text-sm font-bold">Leave adjustment history</Label>
              <div className="mt-2 max-h-40 space-y-1.5 overflow-y-auto">
                {leaveLog.map((l) => (
                  <div key={l.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-border/60 pb-1.5 text-xs last:border-0" data-testid={`leave-log-${l.id}`}>
                    <span className={`rounded px-1.5 py-0.5 font-semibold uppercase ${l.kind === "sick" ? "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"}`}>{l.kind}</span>
                    <span className="font-mono font-medium">{l.old_balance} → {l.new_balance} h</span>
                    <span className="text-muted-foreground">{fmtDate(l.at)} · {l.by}</span>
                    {l.reason && <span className="w-full italic text-muted-foreground">"{l.reason}"</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving || !form.first_name || !form.last_name} data-testid="save-employee-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editId ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!termFor} onOpenChange={(o) => !o && setTermFor(null)}>
        <DialogContent className="max-w-md" data-testid="terminate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Terminate Employee</DialogTitle>
            <DialogDescription>
              {termFor ? `Mark ${termFor.first_name} ${termFor.last_name} as non-active. Record the termination date and reason. They will move to the Non-active tab and be removed from active rosters.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Termination / Last Day</Label>
              <Input type="date" value={termForm.term_date} onChange={(e) => setTermForm((f) => ({ ...f, term_date: e.target.value }))} data-testid="term-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Textarea rows={3} value={termForm.term_reason} onChange={(e) => setTermForm((f) => ({ ...f, term_reason: e.target.value }))} placeholder="e.g., Voluntary resignation, no-call/no-show, position eliminated…" data-testid="term-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermFor(null)}>Cancel</Button>
            <Button onClick={submitTerminate} disabled={termSaving || !termForm.term_reason.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-terminate-btn">
              {termSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Terminate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent className="max-w-lg" data-testid="reviews-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reviews — {reviewFor ? `${reviewFor.first_name} ${reviewFor.last_name}` : ""}</DialogTitle>
            <DialogDescription>The employee must read and acknowledge each review at their next kiosk clock-in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-2 rounded-md border border-border p-3">
              <Input placeholder="Review title (e.g. Q2 Performance Review)" value={reviewForm.title}
                onChange={(e) => setReviewForm((f) => ({ ...f, title: e.target.value }))} data-testid="review-title-input" />
              <Textarea rows={4} placeholder="Write the review / message the employee must read…" value={reviewForm.message}
                onChange={(e) => setReviewForm((f) => ({ ...f, message: e.target.value }))} data-testid="review-message-input" />
              <div className="flex justify-end">
                <Button onClick={submitReview} disabled={reviewSaving} data-testid="review-send-btn">
                  {reviewSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send Review
                </Button>
              </div>
            </div>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {reviewsLoading ? (
                <div className="flex h-20 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
              ) : reviews.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">No reviews yet.</p>
              ) : reviews.map((r) => (
                <div key={r.id} className="rounded-md border border-border p-3" data-testid={`review-item-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold">{r.title}</div>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => deleteReview(r.id)} data-testid={`review-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{r.message}</p>
                  <div className="mt-2 text-xs">
                    {r.acknowledged ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400" data-testid={`review-ack-${r.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Acknowledged by {r.typed_name} · {fmtDate(r.acknowledged_at)}
                      </span>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-400">Awaiting acknowledgement at clock-in</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>


      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-5xl" data-testid="bulk-employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk Add Employees</DialogTitle>
            <DialogDescription>
              Copy rows from Excel/Google Sheets and paste into the grid (columns: Last Name, First Name, Department, Hire Date, Rate, Annual Salary, Phone, Email).
              Click any cell and paste — data flows across columns and down rows automatically. Dates auto-convert to the correct format.
            </DialogDescription>
          </DialogHeader>
          <BulkGrid cols={bulkCols} rows={bulkRows} setRows={setBulkRows} />
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <Button variant="outline" size="sm" onClick={addBulkRows} data-testid="bulk-add-rows-btn">
              <Rows3 className="mr-2 h-4 w-4" /> Add 5 Rows
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button onClick={saveBulk} disabled={bulkSaving} data-testid="bulk-save-btn">
                {bulkSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save All
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="max-w-5xl" data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk Edit Employees</DialogTitle>
            <DialogDescription>
              Every current employee is loaded below. Edit any cell (rate, department, hire date, phone, etc.) or paste updates from a spreadsheet, then save all changes at once.
            </DialogDescription>
          </DialogHeader>
          <BulkGrid cols={bulkCols} rows={bulkEditRows} setRows={setBulkEditRows} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEditOpen(false)}>Cancel</Button>
            <Button onClick={saveBulkEdit} disabled={bulkEditSaving} data-testid="bulk-edit-save-btn">
              {bulkEditSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save All Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deptOpen} onOpenChange={setDeptOpen}>
        <DialogContent className="max-w-md" data-testid="add-department-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Department</DialogTitle>
            <DialogDescription>Create a new department (e.g., Night Auditor). It becomes available for all employees at every property.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Department Name</Label>
            <Input
              value={newDept}
              onChange={(e) => setNewDept(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addDepartment(); }}
              placeholder="e.g., Night Auditor"
              data-testid="new-department-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeptOpen(false)}>Cancel</Button>
            <Button onClick={addDepartment} disabled={deptSaving || !newDept.trim()} data-testid="save-department-btn">
              {deptSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
